import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, SettingsCard, Toggle, useToast } from "@humansignal/ui";
import { useAPI, useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { ProjectContext } from "../../providers/ProjectProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { cn } from "../../utils/bem";
import { AssignReviewersModal } from "./AssignReviewersModal";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./ReviewSettings.prefix.css";

// Canonical defaults mirror ``Project.REVIEW_SETTINGS_DEFAULTS`` in the
// backend. Kept in sync manually — adding a new key requires updating
// both so the page renders something until the first save stores it.
const DEFAULTS = {
  instructions: "",
  show_instructions: false,
  only_assigned: false,
  enabled: true,
  only_finished: false,
  ordering: "sequential",
  task_limit_percent: 100,
  require_comment_on_reject: false,
  show_data_manager: true,
  show_unused_columns: false,
  show_agreement: false,
  tie_breaker_only: false,
  conflict_policy: "single_truth",
};

const ORDERING_OPTIONS = [
  { value: "sequential", label: "Sequential (by Task ID)" },
  { value: "random", label: "Random" },
  { value: "agreement", label: "By agreement (uncertain first)" },
  { value: "model_confidence", label: "By model confidence" },
];

// How to handle a reviewer accepting multiple conflicting annotations on
// one task. One-line descriptions render dynamically under the dropdown.
const CONFLICT_POLICY_OPTIONS = [
  {
    value: "single_truth",
    label: "Single ground truth (accept one)",
    description:
      "Accepting or fixing one annotation makes it the task's ground truth and clears it on the others, one authoritative label per task. Default.",
  },
  {
    value: "warn",
    label: "Allow, but warn on conflict",
    description:
      "Accepting a second differing annotation asks the reviewer to confirm, conflicts stay visible but multi-annotator data is kept.",
  },
  {
    value: "allow_all",
    label: "Allow all (keep for agreement)",
    description:
      "Every accepted annotation stands. A single label is produced later by export aggregation or a Fix & Accept. Best for inter-annotator agreement.",
  },
];

// Thin adapter over the shared ``Toggle`` so the page can bind to a
// boolean-returning ``onChange`` (the shared component hands you the
// raw DOM event). Keeps the callsites below compact and consistent
// with other settings pages that use `@humansignal/ui`'s Toggle.
const Switch = ({ label, description, checked, onChange, disabled }) => (
  <div className={cn("review-settings").elem("switch").toClassName()}>
    <Toggle
      label={label}
      description={description}
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
    />
  </div>
);

export const ReviewSettings = () => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();
  const { project, fetchProject } = useContext(ProjectContext);
  useUpdatePageTitle(createTitleFromSegments([project?.title, "Review Settings"]));

  // Local editable copy. Server always returns defaults-merged shape
  // (see ProjectSerializer.to_representation), so pulling directly is
  // safe; we fall back to DEFAULTS only for the brief moment between
  // mount and first project load.
  const initial = useMemo(() => ({ ...DEFAULTS, ...(project?.review_settings ?? {}) }), [project?.id]);
  const [state, setState] = useState(initial);
  const [savingSection, setSavingSection] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  useEffect(() => {
    setState(initial);
  }, [initial]);

  const taskCount = project?.task_number ?? 0;
  const taskEstimate = Math.round((taskCount * (state.task_limit_percent || 0)) / 100);

  const set = (key, value) => setState((s) => ({ ...s, [key]: value }));

  const save = useCallback(
    async (label, keys) => {
      if (!project?.id) return;
      setSavingSection(label);
      const patch = {};
      for (const k of keys) patch[k] = state[k];
      const res = await api.callApi("updateProject", {
        params: { pk: project.id },
        body: { review_settings: patch },
        suppressError: true,
      });
      setSavingSection(null);
      if (!res || res.error) {
        toast.show({ message: res?.error ?? "Could not save review settings", type: "error" });
        return;
      }
      toast.show({ message: `${label} saved` });
      fetchProject?.(project.id, true);
    },
    [api, project?.id, state, toast, fetchProject],
  );

  return (
    <div className={`${cn("review-settings").toClassName()} ${cn("settings-page").toClassName()}`}>
      <SettingsPageHeader
        title={t("pages.settings.review_settings.review")}
        subtitle={t("pages.settings.review_settings.configure_how_reviewers_work_through_ann")}
      />

      <div className={cn("settings-page").elem("body").toClassName()}>
        <SettingsCard
          title={t("pages.settings.review_settings.instructions")}
          description={t("pages.settings.review_settings.write_instructions_to_help_reviewers_com")}
        >
          <textarea
            className={cn("review-settings").elem("textarea").toClassName()}
            value={state.instructions ?? ""}
            onChange={(e) => set("instructions", e.target.value)}
            rows={6}
            placeholder={t("pages.settings.review_settings.eg_reject_when_the_bounding_box_excludes")}
          />
          <div className={cn("review-settings").elem("row").toClassName()}>
            <Switch
              label={t("pages.settings.review_settings.show_before_reviewing")}
              checked={state.show_instructions}
              onChange={(v) => set("show_instructions", v)}
            />
          </div>
          <div className={cn("review-settings").elem("save-row").toClassName()}>
            <Button
              style={{ width: 120 }}
              size="small"
              onClick={() => save("Instructions", ["instructions", "show_instructions"])}
              disabled={savingSection === "Instructions"}
            >
              {savingSection === "Instructions" ? "Saving…" : "Save"}
            </Button>
          </div>
        </SettingsCard>

        <SettingsCard
          title="Review workflow"
          description="Whether this project uses human review at all. Off: no review stream, no Review button, annotations are final as submitted. Audio QC and crowd validation are separate and keep working."
        >
          <Switch
            label="Enable review for this project"
            description={
              state.enabled === false
                ? "Review is off: reviewers see nothing to review here."
                : "Reviewers see the review stream and can accept, fix or reject."
            }
            checked={state.enabled !== false}
            onChange={(v) => set("enabled", v)}
          />
          <div className={cn("review-settings").elem("save-row").toClassName()}>
            <Button
              style={{ width: 120 }}
              size="small"
              onClick={() => save("Review workflow", ["enabled"])}
              disabled={savingSection === "Review workflow"}
            >
              {savingSection === "Review workflow" ? "Saving…" : "Save"}
            </Button>
          </div>
        </SettingsCard>

        <SettingsCard
          title={t("pages.settings.review_settings.reviewing_options")}
          description={
            "Define when a task counts as Reviewed. This affects what reviewers see in the review " +
            "stream, the Reviewed column in the Data Manager, exports, and dashboards."
          }
        >
          <Switch
            label={t("pages.settings.review_settings.show_only_manually_assigned_tasks_in_the")}
            description={t("pages.settings.review_settings.if_disabled_after_finishing_assigned_tas")}
            checked={state.only_assigned}
            onChange={(v) => set("only_assigned", v)}
          />
          <Switch
            label={t("pages.settings.review_settings.show_only_finished_tasks_in_the_review_s")}
            description={t("pages.settings.review_settings.if_disabled_reviewers_can_see_tasks_that")}
            checked={state.only_finished}
            onChange={(v) => set("only_finished", v)}
          />
          <Switch
            label={t("pages.settings.review_settings.tiebreaker_mode")}
            description={
              "When on, reviewers only see tasks where annotators disagreed, they " +
              "act purely as a tie-breaker. When off, every complete task is surfaced " +
              "for review regardless of whether annotators agreed."
            }
            checked={state.tie_breaker_only}
            onChange={(v) => set("tie_breaker_only", v)}
          />

          <div className={cn("review-settings").elem("field").toClassName()}>
            <span className={cn("review-settings").elem("field-label").toClassName()}>
              {t("pages.settings.review_settings.task_ordering")}
            </span>
            <select
              className={cn("review-settings").elem("select").toClassName()}
              value={state.ordering}
              onChange={(e) => set("ordering", e.target.value)}
            >
              {ORDERING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className={cn("review-settings").elem("field").toClassName()}>
            <span className={cn("review-settings").elem("field-label").toClassName()}>Conflicting annotations</span>
            <select
              className={cn("review-settings").elem("select").toClassName()}
              value={state.conflict_policy}
              onChange={(e) => set("conflict_policy", e.target.value)}
            >
              {CONFLICT_POLICY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className={cn("review-settings").elem("field-hint").toClassName()}>
              {CONFLICT_POLICY_OPTIONS.find((o) => o.value === state.conflict_policy)?.description}
            </span>
          </div>

          <div className={cn("review-settings").elem("field").toClassName()}>
            <span className={cn("review-settings").elem("field-label").toClassName()}>
              {t("pages.settings.review_settings.task_limit")}
            </span>
            <span className={cn("review-settings").elem("field-hint").toClassName()}>
              Percent of project tasks to include in the review stream when sampling is random. Note: if any tasks are
              selected in the Data Manager or reviewers use the Quickview, this limit will not be applied.
            </span>
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={state.task_limit_percent}
              onChange={(e) => set("task_limit_percent", Number(e.target.value))}
              className={cn("review-settings").elem("number").toClassName()}
            />
            <span className={cn("review-settings").elem("field-hint").toClassName()}>
              Approximate number of tasks to include: {taskEstimate.toLocaleString()} of total{" "}
              {taskCount.toLocaleString()}
            </span>
          </div>

          <div className={cn("review-settings").elem("save-row").toClassName()}>
            <Button
              style={{ width: 120 }}
              size="small"
              onClick={() =>
                save("Reviewing Options", [
                  "tie_breaker_only",
                  "only_assigned",
                  "only_finished",
                  "ordering",
                  "conflict_policy",
                  "task_limit_percent",
                ])
              }
              disabled={savingSection === "Reviewing Options"}
            >
              {savingSection === "Reviewing Options" ? "Saving…" : "Save"}
            </Button>
          </div>
        </SettingsCard>

        <SettingsCard
          title={t("pages.settings.review_settings.reject_options")}
          description={t("pages.settings.review_settings.what_reviewers_must_do_when_they_reject_")}
        >
          <Switch
            label={t("pages.settings.review_settings.reviewers_must_leave_a_comment_on_reject")}
            checked={state.require_comment_on_reject}
            onChange={(v) => set("require_comment_on_reject", v)}
          />
          <div className={cn("review-settings").elem("save-row").toClassName()}>
            <Button
              style={{ width: 120 }}
              size="small"
              onClick={() => save("Reject Options", ["require_comment_on_reject"])}
              disabled={savingSection === "Reject Options"}
            >
              {savingSection === "Reject Options" ? "Saving…" : "Save"}
            </Button>
          </div>
        </SettingsCard>

        <SettingsCard
          title={t("pages.settings.review_settings.data_manager")}
          description={t("pages.settings.review_settings.what_reviewers_can_see_in_the_data_manag")}
        >
          <Switch
            label={t("pages.settings.review_settings.show_the_data_manager_to_reviewers")}
            checked={state.show_data_manager}
            onChange={(v) => set("show_data_manager", v)}
          />
          <Switch
            label={t("pages.settings.review_settings.show_unused_task_data_columns_to_reviewe")}
            checked={state.show_unused_columns}
            onChange={(v) => set("show_unused_columns", v)}
            disabled={!state.show_data_manager}
          />
          <Switch
            label={t("pages.settings.review_settings.show_agreement_to_reviewers_in_the_data_")}
            checked={state.show_agreement}
            onChange={(v) => set("show_agreement", v)}
            disabled={!state.show_data_manager}
          />
          <div className={cn("review-settings").elem("save-row").toClassName()}>
            <Button
              style={{ width: 120 }}
              size="small"
              onClick={() => save("Data manager", ["show_data_manager", "show_unused_columns", "show_agreement"])}
              disabled={savingSection === "Data manager"}
            >
              {savingSection === "Data manager" ? "Saving…" : "Save"}
            </Button>
          </div>
        </SettingsCard>

        <SettingsCard
          title={t("pages.settings.review_settings.assign_reviewers")}
          description={
            "Attach reviewers to tasks in bulk so the review stream serves work " +
            "only to the right people. Pick reviewers from the project's members, " +
            "then choose whether to assign them to every task, only the unreviewed " +
            "ones, or a specific set of task IDs."
          }
        >
          <div className={cn("review-settings").elem("save-row").toClassName()}>
            <Button size="small" onClick={() => setAssignOpen(true)}>
              Assign reviewers…
            </Button>
          </div>
        </SettingsCard>
      </div>

      {assignOpen && <AssignReviewersModal projectId={project?.id} onClose={() => setAssignOpen(false)} />}
    </div>
  );
};

ReviewSettings.title = "Review";
ReviewSettings.path = "/review-settings";
ReviewSettings.exact = true;
